import { Utils } from '@Util/utils'
import { MultiplierAnimation, MultiplierAnimationResult } from '../../multiplier-animation'
import { tweenPromise, tweenWaitPromise } from '@Animation/utils'
import { MI_MULTIPLIER_DICE_ASSETS, MI_MULTIPLIER_DICE_TEXTURE_KEYS } from './asset-preloader'

export class MichiganDiceMultiplierAnimation extends MultiplierAnimation {
  private background: Phaser.GameObjects.Image
  private backgroundLogo: Phaser.GameObjects.Image
  private backgroundContainer: Phaser.GameObjects.Container

  private overlay: Phaser.GameObjects.Graphics
  private lighting: Phaser.GameObjects.Image

  private shadow: Phaser.GameObjects.Image
  private die: Phaser.GameObjects.Sprite
  private dieContainer: Phaser.GameObjects.Container

  constructor(scene: Phaser.Scene, x: number, y: number, config: MultiplierAnimationResult) {
    super(scene, x, y, config)

    if (!scene.textures.getTextureKeys().includes(MI_MULTIPLIER_DICE_TEXTURE_KEYS.DICE)) {
      scene.textures.addSpriteSheetFromAtlas(MI_MULTIPLIER_DICE_TEXTURE_KEYS.DICE, {
        atlas: MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
        frame: MI_MULTIPLIER_DICE_ASSETS.DICE,
        frameWidth: 252,
        frameHeight: 266,
      })
    }

    if (!scene.anims.exists(MI_MULTIPLIER_DICE_ASSETS.DICE_INTRO_ANIMATION_KEY)) {
      scene.anims.create({
        key: MI_MULTIPLIER_DICE_ASSETS.DICE_INTRO_ANIMATION_KEY,
        frames: scene.anims.generateFrameNumbers(MI_MULTIPLIER_DICE_TEXTURE_KEYS.DICE_INTRO, {
          start: 0,
          end: 17,
        }),
        duration: 1000,
        hideOnComplete: false,
      })
    }

    this.backgroundContainer = this.scene.add.container(0, 200)

    this.background = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_DICE_ASSETS.BACKGROUND,
    )
    this.background.setOrigin(0.5, 0.5)
    this.background.setScale(0.67, 0.8)

    this.backgroundLogo = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_DICE_ASSETS.LOGO,
    )
    this.backgroundLogo.setOrigin(0.5, 0.5)
    this.backgroundLogo.setAlpha(0.5)

    const { width, height } = this.scene.sys.game.canvas

    this.overlay = this.scene.add.graphics()
    this.overlay.fillStyle(0x000000, 0.5)
    this.overlay.fillRect(-width / 2, -height / 2, width, height)

    this.shadow = this.scene.add.image(
      0,
      100,
      MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_DICE_ASSETS.SHADOW,
    )
    this.shadow.setOrigin(0.5, 0.5)
    this.shadow.setScale(0.4, 0.8)

    this.lighting = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_DICE_ASSETS.LIGHTING,
    )
    this.lighting.setAlpha(0)
    this.lighting.setScale(1)

    this.dieContainer = this.scene.add.container(-60, 50)

    this.die = this.scene.add.sprite(0, 0, MI_MULTIPLIER_DICE_TEXTURE_KEYS.DICE_INTRO, 0)

    this.dieContainer.setScale(1.2)

    this.backgroundContainer.add([this.background, this.backgroundLogo, this.overlay])
    this.dieContainer.add([this.die])
    this.add([this.backgroundContainer, this.shadow, this.dieContainer, this.lighting])
  }

  async play() {
    await this.showIntroDie()
    await this.throwDie()
    await this.dieBounce()
    await this.showResult()

    await tweenWaitPromise(this.scene, 1800)()

    await tweenPromise(this.scene, {
      targets: this,
      alpha: 0,
      duration: 600,
      ease: Phaser.Math.Easing.Circular.In,
    })()

    this.destroy()
    return
  }

  async showIntroDie() {
    const duration = 1133

    this.shadow.setScale(2, 0.5)
    this.shadow.setPosition(-60, 225)
    const shadowFollow = tweenPromise(this.scene, {
      targets: this.shadow,
      x: 60,
      duration: duration,
    })

    await tweenWaitPromise(this.scene, 75)()
    this.die.play(MI_MULTIPLIER_DICE_ASSETS.DICE_INTRO_ANIMATION_KEY)
    await tweenPromise(this.scene, {
      targets: this.dieContainer,
      x: 60,
      duration: duration,
      onStart: shadowFollow,
    })()
  }

  async throwDie() {
    const shadowFollow = tweenPromise(this.scene, {
      targets: this.shadow,
      x: 80,
      y: 0,
      scaleX: 0.55,
      scaleY: 0.4,
      duration: 900,
    })

    const overlayFade = tweenPromise(this.scene, {
      targets: this.overlay,
      alpha: 0,
      duration: 500,
    })

    overlayFade()

    //Throw Die
    await tweenPromise(this.scene, {
      targets: this.dieContainer,
      x: 80,
      y: -400,
      scale: 0.6,
      rotation: Math.PI,
      duration: 300,
      onStart: shadowFollow,
    })()

    this.die.setTexture(MI_MULTIPLIER_DICE_TEXTURE_KEYS.DICE, 0)

    this.zoomBackgroundBounce()

    //Die land
    await tweenPromise(this.scene, {
      targets: this.dieContainer,
      x: 50,
      y: 0,
      scale: 0.45,
      rotation: Math.PI / 2,
      duration: 600,
    })()
  }

  async dieBounce() {
    const validTextureFrames = [0, 1, 2, 3]

    const showLighting = tweenPromise(this.scene, {
      targets: this.lighting,
      alpha: 1,
      duration: 4000,
    })

    showLighting()

    await this.bounceDie(-50, 200)
    this.die.setFrame(Utils.getRandomElementFromArray(validTextureFrames))
    await this.bounceDie(40, 150)
    this.die.setFrame(Utils.getRandomElementFromArray(validTextureFrames))

    await this.bounceDie(-30, 100)
    this.die.setFrame(Utils.getRandomElementFromArray(validTextureFrames))

    await this.bounceDie(20, 50)
    this.die.setFrame(Utils.getRandomElementFromArray(validTextureFrames))
    await this.bounceDie(-10, 25)
  }

  async zoomBackgroundBounce() {
    const scaleContainer = tweenPromise(this.scene, {
      targets: this.backgroundContainer,
      scale: 1.1,
      y: 100,
      duration: 600,
    })

    const moveTable = tweenPromise(this.scene, {
      targets: [this.background],
      y: -50,
      duration: 600,
    })

    await Promise.all([scaleContainer(), moveTable()])
  }

  async zoomBackgroundReveal() {
    await tweenPromise(this.scene, {
      targets: this.backgroundContainer,
      scale: 1.5,
      y: 350,
      duration: 500,
    })()
  }

  async showResult() {
    this.die.setFrame(4)

    const resultSide = this.scene.add.image(
      -8,
      -52,
      MI_MULTIPLIER_DICE_TEXTURE_KEYS.ATLAS,
      `${this.multiplierValue}${MI_MULTIPLIER_DICE_ASSETS.NUMBER_SUFFIX}`,
    )

    resultSide.setScale(0.25)
    // resultSide.setScale(0.08, 0.08)
    resultSide.setRotation(Utils.degreesToRadians(37))

    this.dieContainer.add(resultSide)

    await this.bounceDie(5, 10)
    await this.bounceDie(-3, 5)
    await this.bounceDie(0, 3)
    await tweenPromise(this.scene, {
      targets: this.dieContainer,
      rotation: 0,
      duration: 100,
    })()

    this.zoomBackgroundReveal()

    const hideDie = tweenPromise(this.scene, {
      targets: [this.die, this.shadow],
      alpha: 0,
      duration: 500,
    })

    await tweenPromise(this.scene, {
      targets: resultSide,
      x: -this.dieContainer.x,
      y: -this.dieContainer.y,
      rotation: -this.dieContainer.rotation,
      scale: 3,
      duration: 500,
      onStart: hideDie,
    })()
  }

  async bounceDie(newX: number, bounceHeight: number) {
    const duration = 3 * bounceHeight
    const floorHeight = this.dieContainer.y
    const rotationDirection = newX < 0 ? -1 : 1
    const rotation = rotationDirection * (bounceHeight / 120) * Math.PI

    const shadowFollow = tweenPromise(this.scene, {
      targets: this.shadow,
      x: newX,
      y: floorHeight + 25,
      scale: 0.55,
      duration: duration,
    })

    const bounceHorizontal = tweenPromise(this.scene, {
      targets: this.dieContainer,
      duration: duration,
      rotation: rotation,
      scale: this.dieContainer.scale - bounceHeight / 5000,
      x: newX,
      onStart: shadowFollow,
    })
    const bounceVerticalUp = tweenPromise(this.scene, {
      targets: this.dieContainer,
      duration: duration / 2,
      y: floorHeight - bounceHeight,
      ease: Phaser.Math.Easing.Circular.Out,
    })

    const bounceVerticalDown = tweenPromise(this.scene, {
      targets: this.dieContainer,
      duration: duration / 2,
      y: floorHeight,
      ease: Phaser.Math.Easing.Circular.In,
    })

    const tableMove = tweenPromise(this.scene, {
      targets: this.backgroundContainer,
      y: this.backgroundContainer.y + bounceHeight / 7,
      duration: duration,
    })

    tableMove()
    bounceHorizontal()
    await bounceVerticalUp()
    await bounceVerticalDown()
  }

  destroy(fromScene?: boolean): void {
    this.backgroundContainer.removeAll(true)
    this.dieContainer.removeAll(true)
    super.destroy(fromScene)
  }
}
